import { z } from 'zod'

export const SUPPLIER_CATEGORIES = [
  'venue',
  'catering',
  'bar',
  'cake-sweets',
  'photographer',
  'videographer',
  'florist',
  'decor',
  'stationery',
  'ceremony-music',
  'party-music',
  'moderator',
  'officiant',
  'hair',
  'makeup',
  'bride-attire',
  'groom-attire',
  'rings',
  'rentals',
  'transport',
  'accommodation',
  'coordination',
  'entertainment',
  'other',
] as const

export const WEDDING_SUPPLIER_STATUSES = [
  'contacted',
  'shortlisted',
  'awaiting-offer',
  'selected',
  'contract-pending',
  'booked',
  'cancelled',
] as const

export const SUPPLIER_REQUIREMENT_STATUSES = [
  'undecided',
  'needed',
  'searching',
  'fulfilled',
  'booked',
  'declined',
  'not-needed',
] as const

export const supplierCategorySchema = z.enum(SUPPLIER_CATEGORIES)
export const weddingSupplierStatusSchema = z.enum(WEDDING_SUPPLIER_STATUSES)
export const supplierRequirementStatusSchema = z.enum(SUPPLIER_REQUIREMENT_STATUSES)

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const optionalEmail = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.email('Enter a valid email address').optional(),
)

const optionalUrl = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.url('Enter a valid URL').optional(),
)

const clearableOptionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

const clearableOptionalEmail = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.email('Enter a valid email address').nullable().optional(),
)

const clearableOptionalUrl = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.url('Enter a valid URL').nullable().optional(),
)

const optionalMoney = z
  .number()
  .int()
  .min(0, 'Amount cannot be negative')
  .max(100_000_000, 'Amount is too high')
  .optional()

const optionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.iso.date().optional(),
)

const optionalTime = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm time')
    .optional(),
)

export const createSupplierSchema = z.object({
  name: z.string().trim().min(1, 'Enter supplier name').max(160),
  categories: z.array(supplierCategorySchema).min(1, 'Pick at least one category'),
  companyId: optionalText(40),
  taxId: optionalText(40),
  contactName: optionalText(120),
  email: optionalEmail,
  phone: optionalText(60),
  website: optionalUrl,
  instagram: optionalText(120),
  address: optionalText(300),
  serviceArea: optionalText(300),
  notes: optionalText(2000),
})

export const updateSupplierSchema = z.object({
  name: z.string().trim().min(1, 'Enter supplier name').max(160).optional(),
  categories: z.array(supplierCategorySchema).min(1, 'Pick at least one category').optional(),
  companyId: clearableOptionalText(40),
  taxId: clearableOptionalText(40),
  contactName: clearableOptionalText(120),
  email: clearableOptionalEmail,
  phone: clearableOptionalText(60),
  website: clearableOptionalUrl,
  instagram: clearableOptionalText(120),
  address: clearableOptionalText(300),
  serviceArea: clearableOptionalText(300),
  notes: clearableOptionalText(2000),
})

export const createWeddingSupplierSchema = z.object({
  supplierId: z.uuid(),
  roles: z.array(supplierCategorySchema).min(1, 'Pick at least one role'),
  status: weddingSupplierStatusSchema.default('selected'),
  scope: optionalText(3000),
  quoteAmount: optionalMoney,
  agreedAmount: optionalMoney,
  finalAmount: optionalMoney,
  currency: z.string().trim().length(3).default('CZK'),
  depositAmount: optionalMoney,
  depositDueDate: optionalDate,
  depositPaidAt: optionalDate,
  finalDueDate: optionalDate,
  finalPaidAt: optionalDate,
  paymentMethod: optionalText(120),
  arrivalTime: optionalTime,
  setupTime: optionalTime,
  departureTime: optionalTime,
  contactName: optionalText(120),
  contactEmail: optionalEmail,
  contactPhone: optionalText(60),
  notes: optionalText(3000),
})

export const updateWeddingSupplierSchema = z.object({
  supplierId: z.uuid().optional(),
  roles: z.array(supplierCategorySchema).min(1, 'Pick at least one role').optional(),
  status: weddingSupplierStatusSchema.optional(),
  scope: optionalText(3000),
  quoteAmount: optionalMoney,
  agreedAmount: optionalMoney,
  finalAmount: optionalMoney,
  currency: z.string().trim().length(3).optional(),
  depositAmount: optionalMoney,
  depositDueDate: optionalDate,
  depositPaidAt: optionalDate,
  finalDueDate: optionalDate,
  finalPaidAt: optionalDate,
  paymentMethod: optionalText(120),
  arrivalTime: optionalTime,
  setupTime: optionalTime,
  departureTime: optionalTime,
  contactName: optionalText(120),
  contactEmail: optionalEmail,
  contactPhone: optionalText(60),
  notes: optionalText(3000),
})

export const createSupplierRequirementSchema = z.object({
  category: supplierCategorySchema,
  status: supplierRequirementStatusSchema.default('needed'),
  sectionKey: optionalText(80),
  requirementKey: optionalText(120),
  title: z.string().trim().min(1, 'Enter requirement title').max(180),
  desiredScope: optionalText(3000),
  targetBudget: optionalMoney,
  linkedWeddingSupplierId: z.uuid().optional(),
  notes: optionalText(3000),
})

export const updateSupplierRequirementSchema = z.object({
  category: supplierCategorySchema.optional(),
  status: supplierRequirementStatusSchema.optional(),
  sectionKey: optionalText(80),
  requirementKey: optionalText(120),
  title: z.string().trim().min(1, 'Enter requirement title').max(180).optional(),
  desiredScope: optionalText(3000),
  targetBudget: optionalMoney,
  linkedWeddingSupplierId: z.uuid().optional(),
  notes: optionalText(3000),
})

export type SupplierCategory = (typeof SUPPLIER_CATEGORIES)[number]
export type WeddingSupplierStatus = (typeof WEDDING_SUPPLIER_STATUSES)[number]
export type SupplierRequirementStatus = (typeof SUPPLIER_REQUIREMENT_STATUSES)[number]
export type CreateSupplierInput = z.infer<typeof createSupplierSchema>
export type UpdateSupplierInput = z.infer<typeof updateSupplierSchema>
export type CreateWeddingSupplierInput = z.infer<typeof createWeddingSupplierSchema>
export type UpdateWeddingSupplierInput = z.infer<typeof updateWeddingSupplierSchema>
export type CreateSupplierRequirementInput = z.infer<typeof createSupplierRequirementSchema>
export type UpdateSupplierRequirementInput = z.infer<typeof updateSupplierRequirementSchema>
